<template>
  <div
    v-if="open"
    class="modal-backdrop open"
    role="dialog"
    aria-modal="true"
    aria-labelledby="applyTitle"
    @click.self="$emit('close')"
  >
    <div class="modal compact">
      <div class="row">
        <h2 id="applyTitle">{{ t("apply_title") }}</h2>
        <button type="button" class="close" aria-label="关闭" @click="$emit('close')">×</button>
      </div>
      <p class="subtext">{{ t("apply_sub") }}</p>
      <form @submit.prevent="submit">
        <div class="form-group">
          <label for="projectName">{{ t("f_project") }}</label>
          <input
            id="projectName"
            v-model="form.name"
            class="input"
            required
            maxlength="60"
            :placeholder="t('f_project_ph')"
            :disabled="submitting"
          />
        </div>
        <div class="form-group">
          <label for="issuerName">{{ t("f_issuer") }}</label>
          <input
            id="issuerName"
            v-model="form.issuer"
            class="input"
            required
            maxlength="80"
            :placeholder="t('f_issuer_ph')"
            :disabled="submitting"
          />
        </div>
        <div class="form-group">
          <label for="projectDesc">{{ t("f_description") }}</label>
          <textarea
            id="projectDesc"
            v-model="form.description"
            class="input"
            required
            rows="4"
            maxlength="2000"
            :placeholder="t('f_description_ph')"
            :disabled="submitting"
          ></textarea>
        </div>
        <div class="form-group">
          <label>{{ t("pay_coin") }}</label>
          <div class="coin-tabs">
            <div
              v-for="c in coins"
              :key="c"
              class="coin-opt"
              :class="{
                active: applyCoin === c,
                disabled: !isLaunchableCoin(c),
              }"
              role="radio"
              :aria-checked="applyCoin === c"
              :aria-disabled="!isLaunchableCoin(c)"
              tabindex="0"
              @click="selectCoin(c)"
              @keydown.enter.prevent="selectCoin(c)"
            >
              <span class="co">{{ c }}</span>
              <span class="cd">{{ coinLaunchHint(c) }}</span>
            </div>
          </div>
        </div>
        <div class="form-group">
          <label>{{ t("f_mode") }}</label>
          <div class="mode-tabs">
            <button
              type="button"
              :class="{ active: applyMode === 'fixed' }"
              :disabled="submitting"
              @click="applyMode = 'fixed'"
            >
              {{ t("mode_fixed") }}
            </button>
            <button
              type="button"
              :class="{ active: applyMode === 'step' }"
              :disabled="submitting"
              @click="applyMode = 'step'"
            >
              {{ t("mode_step") }}
            </button>
          </div>
        </div>
        <div class="currency-row">
          <div class="cname">{{ applyCoin }} · {{ modeLabel }}</div>
          <template v-if="applyMode === 'fixed'">
            <div class="form-group" style="margin: 4px 0 0">
              <label>{{ applyCoin }} {{ t("unit_price") }}</label>
              <input
                v-model.number="priceFixed"
                class="input"
                type="number"
                min="0.00000001"
                step="any"
                required
                :disabled="submitting"
              />
            </div>
          </template>
          <template v-else>
            <div class="form-group" style="margin: 4px 0 0">
              <label>{{ applyCoin }} {{ t("start_price") }}</label>
              <input
                v-model.number="priceStart"
                class="input"
                type="number"
                min="0.00000001"
                step="any"
                required
                :disabled="submitting"
              />
            </div>
            <div class="form-group" style="margin: 4px 0 0">
              <label>{{ applyCoin }} {{ t("step_price") }}</label>
              <input
                v-model.number="priceStep"
                class="input"
                type="number"
                min="0.00000001"
                step="any"
                required
                :disabled="submitting"
              />
            </div>
          </template>
        </div>
        <div class="form-group">
          <label for="supply">{{ t("f_supply") }}</label>
          <input
            id="supply"
            v-model.number="form.supply"
            class="input"
            type="number"
            min="1"
            max="1000000"
            step="1"
            required
            :placeholder="t('f_supply_ph')"
            :disabled="submitting"
          />
        </div>
        <div class="form-group">
          <label v-html="t('f_image')"></label>
          <label class="upload-box" :class="{ 'has-file': !!imagePreview }" for="nftImage">
            <input
              id="nftImage"
              type="file"
              accept="image/*"
              hidden
              :disabled="submitting || imageUploading"
              @change="onFile"
            />
            <span v-show="!imagePreview && !imageUploading" class="upload-hint" v-html="t('upload_hint')"></span>
            <span v-show="imageUploading" class="upload-hint">{{ t("toast_img_uploading") }}</span>
            <img v-show="imagePreview && !imageUploading" :src="imagePreview" alt="NFT 封面" />
          </label>
        </div>
        <div class="notice">{{ t("apply_notice") }}</div>
        <button
          type="submit"
          class="btn gold full"
          style="margin-top: 20px"
          :disabled="submitting || imageUploading || !uploadedImageUrl"
        >
          {{ submitting ? t("apply_submitting") : t("apply_submit") }}
        </button>
      </form>
    </div>
  </div>
</template>

<script>
import {
  COINS,
  COIN_META,
  defaultPriceForCoin,
} from "@/film/pricing";
import { uploadMetadataImage } from "@/film/api/metadata";

export default {
  name: "ApplyModal",
  props: {
    open: Boolean,
    submitting: { type: Boolean, default: false },
    t: { type: Function, required: true },
    fmt: { type: Function, required: true },
    launchCoins: { type: Array, default: () => [...COINS] },
  },
  data() {
    return {
      coins: COINS,
      form: { name: "", issuer: "", description: "", supply: 1000 },
      applyCoin: "AAPLB",
      applyMode: "fixed",
      priceFixed: 1,
      priceStart: 1,
      priceStep: 0.1,
      imagePreview: "",
      uploadedImageUrl: "",
      imageUploading: false,
      uploadSeq: 0,
    };
  },
  computed: {
    modeLabel() {
      return this.applyMode === "fixed" ? this.t("f_price_fixed") : this.t("f_price_step");
    },
  },
  watch: {
    open(val) {
      if (val) this.resetForm();
    },
    applyCoin(coin) {
      this.syncDefaultPrices(coin, this.applyMode);
    },
    applyMode(mode) {
      this.syncDefaultPrices(this.applyCoin, mode);
    },
  },
  methods: {
    coinMeta(coin) {
      return COIN_META[coin] || COIN_META.AAPLB;
    },
    isLaunchableCoin(coin) {
      return (this.launchCoins || []).includes(coin);
    },
    coinLaunchHint(coin) {
      const meta = this.coinMeta(coin);
      if (this.isLaunchableCoin(coin)) return meta.subtitle;
      return this.t("coin_launch_off");
    },
    selectCoin(coin) {
      if (this.submitting || !this.isLaunchableCoin(coin)) return;
      this.applyCoin = coin;
    },
    syncDefaultPrices(coin, mode) {
      if (mode === "fixed") {
        this.priceFixed = defaultPriceForCoin(coin, "fixed");
      } else {
        const step = defaultPriceForCoin(coin, "step");
        this.priceStart = step.start;
        this.priceStep = step.inc;
      }
    },
    resetForm() {
      this.form = { name: "", issuer: "", description: "", supply: 1000 };
      const first =
        (this.launchCoins && this.launchCoins[0]) ||
        (this.isLaunchableCoin("AAPLB") ? "AAPLB" : COINS[0]);
      this.applyCoin = first;
      this.applyMode = "fixed";
      this.syncDefaultPrices(first, "fixed");
      this.imagePreview = "";
      this.uploadedImageUrl = "";
      this.imageUploading = false;
      this.uploadSeq += 1;
    },
    async onFile(e) {
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      if (!/^image\//.test(file.type)) {
        this.$emit("toast", "toast_img_type");
        e.target.value = "";
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        this.$emit("toast", "toast_img_size");
        e.target.value = "";
        return;
      }
      const seq = this.uploadSeq + 1;
      this.uploadSeq = seq;
      this.uploadedImageUrl = "";
      this.imageUploading = true;
      this.$emit("toast", "toast_img_uploading");

      const reader = new FileReader();
      reader.onload = () => {
        if (this.uploadSeq === seq) {
          this.imagePreview = reader.result;
        }
      };
      reader.onerror = () => {
        if (this.uploadSeq !== seq) return;
        this.imageUploading = false;
        this.imagePreview = "";
        this.$emit("toast", "toast_img_fail");
        e.target.value = "";
      };
      reader.readAsDataURL(file);

      try {
        const imageUrl = await uploadMetadataImage(file);
        if (this.uploadSeq !== seq) return;
        this.uploadedImageUrl = imageUrl;
        this.$emit("toast", "toast_img_ready");
      } catch (err) {
        console.error(err);
        if (this.uploadSeq !== seq) return;
        this.imagePreview = "";
        this.uploadedImageUrl = "";
        this.$emit("toast", "toast_img_upload_fail");
        e.target.value = "";
      } finally {
        if (this.uploadSeq === seq) {
          this.imageUploading = false;
        }
      }
    },
    buildPricing() {
      const pricing = { coin: this.applyCoin, mode: this.applyMode };
      if (this.applyMode === "fixed") {
        pricing.fixed = Math.max(0, Number(this.priceFixed) || 0);
      } else {
        pricing.step = {
          start: Math.max(0, Number(this.priceStart) || 0),
          inc: Math.max(0, Number(this.priceStep) || 0),
        };
      }
      return pricing;
    },
    submit() {
      if (this.submitting) return;
      if (!this.isLaunchableCoin(this.applyCoin)) {
        this.$emit("toast", "toast_launch_pay_token");
        return;
      }
      if (!this.uploadedImageUrl) {
        this.$emit("toast", "toast_need_image");
        return;
      }
      const description = this.form.description.trim();
      if (!description) {
        this.$emit("toast", "toast_need_image");
        return;
      }
      this.$emit("launch", {
        name: this.form.name.trim(),
        issuer: this.form.issuer.trim(),
        description,
        pricing: this.buildPricing(),
        supply: Math.min(1000000, Math.max(1, Number(this.form.supply) || 1000)),
        imageUrl: this.uploadedImageUrl,
      });
    },
  },
};
</script>
